import React from "react";
import {createPortal} from 'react-dom';

/**
 * Display floating text bubbles above players
 *
 * Uses React Portal to render messages at #game-root level to avoid CSS transform issues
 * from parent containers (especially in the Tutorial which applies scale transforms).
 *
 * The component creates a fixed-position wrapper that can be scaled for the Tutorial context,
 * then positions individual messages absolutely within that wrapper. Position coordinates
 * are divided by tutorialScale to compensate for the wrapper's scaling transform.
 *
 * @param {Array} statusMessages - Array of message objects with structure:
 *   - id: unique identifier
 *   - position: {x, y} viewport coordinates
 *   - message: text to display
 *   - is_connection_timer: boolean for connection status messages
 *   - is_play_announcement: boolean for game action announcements
 * @param {number|null} tutorialScale - Scale factor for Tutorial context (e.g., 0.7).
 *   If null, no scaling is applied (normal gameplay).
 * @returns {ReactPortal} Messages rendered at #game-root level
 */
export default function StatusMessage({statusMessages, tutorialScale = null}) {
	const gameRoot = document.getElementById('game-root');

	if (!gameRoot) return null;

	return createPortal(<div style={{
		position: 'fixed',
		inset: 0,
		transform: tutorialScale ? `scale(${tutorialScale})` : 'none',
		transformOrigin: 'top left',
		pointerEvents: 'none',
		zIndex: 50
	}}>
		{statusMessages.map(msg => {

			const messageY = tutorialScale ? msg.position.y / tutorialScale : msg.position.y;
     const viewportHeight = window.innerHeight;
     const availableSpace = messageY; // Distance from top of screen to message

     // Calculate max float distance (default is 132% which is roughly 1.32x message height)
     // Assume message height is ~50px, so default float is ~66px
     const defaultFloatDistance = 66;
     const maxFloatDistance = Math.min(defaultFloatDistance, availableSpace - 20); // 20px margin from top
     const floatPercentage = (maxFloatDistance / defaultFloatDistance) * 132; // Scale the percentage

			return <div
			key={msg.id}
			id={`status-${msg.id}`}
			className={`
				absolute pointer-events-none backdrop-blur-lg drop-shadow-lg rounded-3xl p-3
      	${msg.is_connection_timer
					? 'bg-amber-50 bg-opacity-20 text-sm -translate-x-1/2 -translate-y-full'
				  : msg.message === 'Call!'
					? 'bg-red-400 bg-opacity-60 text-[clamp(0.875rem, 0.7vw, 1.125rem)] play-announcement'
					: msg.is_play_announcement
						? 'bg-amber-200 bg-opacity-60 text-[clamp(0.875rem, 0.7vw, 1.125rem)] play-announcement'
						: 'bg-amber-50 bg-opacity-20 text-xs md:text-sm message_float'
				}`}
			style={{
				left: tutorialScale ? `${msg.position.x / tutorialScale}px` : `${msg.position.x}px`,
				top: tutorialScale ? `${msg.position.y / tutorialScale}px` : `${msg.position.y}px`,
				'--float-distance': `-${floatPercentage}%`,
				transition: 'top 0.3s ease',
			}}
		>
			<div className="text-white font-semibold break-words text-center" style={{maxWidth: 'min(400px, 25vw)'}}>
				{msg.is_connection_timer && msg.message === 'Reconnecting...'
					? <span className="inline-flex items-center whitespace-nowrap">Reconnecting<span className="dot-bounce"><span>.</span><span>.</span><span>.</span></span></span>
					: msg.message}
			</div>
		</div>})}
	</div>, gameRoot);
}
